/**
 * Konfigurisana Axios instanca za sve API pozive.
 * Base URL, timeout i interceptor-i se određuju iz environment varijabli.
 *
 * Env varijable:
 * - REACT_APP_USE_PROXY=true — prazan baseURL (dev proxy)
 * - REACT_APP_USE_RELATIVE_URL=true — relativna adresa u produkciji
 * - REACT_APP_API_BASE_URL — direktan backend URL
 * - REACT_APP_API_FALLBACK_URL — rezervni URL kad BASE nije setovan
 * - REACT_APP_API_TIMEOUT — timeout u ms (podrazumevano 30000)
 */

import axios, { AxiosHeaders } from 'axios';
import { maybeScheduleNotificationCountRefresh } from '@/shared/utils/notificationCountSync';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';

/**
 * Osigurava `Content-Type: application/json` i JSON stringify tela za POST/PUT/PATCH.
 * Spring backend odbija `text/plain` telo.
 * @param {import('axios').InternalAxiosRequestConfig} config - Axios request config.
 * @returns {import('axios').InternalAxiosRequestConfig} Ažurirani config.
 */
function ensureJsonContentType(config) {
  const method = String(config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch'].includes(method)) return config;

  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (isFormData) return config;

  if (config.data === undefined || config.data === null) return config;

  const headers = AxiosHeaders.from(config.headers);
  const contentType = String(headers.getContentType() || '').toLowerCase();
  if (!contentType || contentType.startsWith('text/plain')) {
    headers.setContentType('application/json', false);
  }

  if (
    typeof config.data === 'object'
    && !(config.data instanceof Blob)
    && !(config.data instanceof ArrayBuffer)
    && !(typeof URLSearchParams !== 'undefined' && config.data instanceof URLSearchParams)
  ) {
    config.data = JSON.stringify(config.data);
  }

  config.headers = headers;
  return config;
}

const useProxy = process.env.REACT_APP_USE_PROXY === 'true';
const useRelativeUrl = process.env.REACT_APP_USE_RELATIVE_URL === 'true' ||
                       (process.env.NODE_ENV === 'production' && !process.env.REACT_APP_API_BASE_URL);
const backendUrl = resolveApiBaseUrl();

const baseURL = useProxy ? '' : (useRelativeUrl ? '' : backendUrl);

/**
 * Singleton Axios instanca sa base URL-om, timeout-om i interceptor-ima.
 * @type {import('axios').AxiosInstance}
 */
const axiosInstance = axios.create({
  baseURL: baseURL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => ensureJsonContentType(config),
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    maybeScheduleNotificationCountRefresh(response);
    return response;
  },
  (error) => Promise.reject(error),
);

export default axiosInstance;
